import { ErrorSidePanel } from '@/components/errors';
import { type AccountActivityFilters } from '@/portal-api.definition';
import { ModuleName, Screen, templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { useGetAccountActivity } from '../-hooks/get-account-activity.hook';
import { useGetApplicationsNames } from '../-hooks/get-application-names.hook';
import { type AccountActivityQuery, accountActivityQuerySchema } from '../-model/queryFilters';
import { normalizeDeviceName, createDeviceGroupMap } from '../-model/utils';
import { ALL_FILTER, type AccountActivityFilterProps } from './-model/props';
import { AccountActivityFiltersLight } from './-skins';
import { useState } from 'react';

export const Route = createFileRoute('/(security)/security/_sheet/account-activity/filters/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  staticData: {
    module: ModuleName.Security,
    screen: Screen.AccountActivity,
  },
  validateSearch: accountActivityQuerySchema,
});

function checkIfFiltersEmpty(filters: AccountActivityQuery) {
  return (
    !filters.activities.length &&
    !filters.applications.length &&
    !filters.devices.length &&
    !filters.orders.length
  );
}

function computeSingleSelectUpdate(
  current: string[],
  value: string,
  isCurrentlySelected: boolean,
): string[] {
  return isCurrentlySelected ? current : [value];
}

function computeWithAllUpdate(
  current: string[],
  value: string,
  isCurrentlySelected: boolean,
): string[] {
  if (value === ALL_FILTER) return [ALL_FILTER];
  if (isCurrentlySelected) {
    const filtered = current.filter((v) => v !== value);
    return filtered.length === 0 ? [ALL_FILTER] : filtered;
  }
  return [...current.filter((v) => v !== ALL_FILTER), value];
}

function computeMultiSelectUpdate(
  current: string[],
  value: string,
  isCurrentlySelected: boolean,
): string[] {
  if (isCurrentlySelected) {
    return current.length > 1 ? current.filter((v) => v !== value) : current;
  }
  return [...current, value];
}

function computeDeviceFilterUpdate(
  current: string[],
  value: string,
  deviceGroupMap: Map<string, string[]>,
  hasAllOption: boolean,
): string[] {
  const originals = deviceGroupMap.get(value) ?? [value];
  const isCurrentlySelected = originals.some((o) => current.includes(o));

  if (!isCurrentlySelected) {
    return [...current.filter((d) => d !== ALL_FILTER), ...originals];
  }

  const filtered = current.filter((d) => d !== ALL_FILTER && !originals.includes(d));
  if (filtered.length === 0) {
    return hasAllOption ? [ALL_FILTER] : current;
  }
  return filtered;
}

function computePropertyUpdate(
  current: string[],
  value: string,
  property: string,
  hasAllValueOption: boolean,
): string[] {
  const isCurrentlySelected = current.includes(value);

  if (property === 'Orders') {
    return computeSingleSelectUpdate(current, value, isCurrentlySelected);
  }
  if (hasAllValueOption) {
    return computeWithAllUpdate(current, value, isCurrentlySelected);
  }
  return computeMultiSelectUpdate(current, value, isCurrentlySelected);
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const query = Route.useSearch();
  const [dirty, setDirty] = useState(!checkIfFiltersEmpty(query));
  const { data: accountActivityData, isLoading: isAccountActivityLoading } = useGetAccountActivity({
    activities: [],
    applications: [],
    devices: [],
    locations: [],
    orders: [],
  });
  const { data: applicationsNamesData, isLoading: isApplicationsNamesLoading } =
    useGetApplicationsNames();
  const filters = accountActivityData?.pages?.[0];

  const filtersFromQuery: AccountActivityFilters = {
    Orders: query.orders ?? [],
    Activities: query.activities ?? [],
    Devices: query.devices ?? [],
    Applications: query.applications ?? [],
    Locations: [],
  };

  const activeFilters: AccountActivityFilters = dirty
    ? filtersFromQuery
    : (filters?.ActiveFilters ?? filtersFromQuery);

  const availableFilters = filters?.AvailableFilters ?? {
    Orders: [],
    Activities: [],
    Devices: [],
    Applications: [],
    Locations: [],
  };

  const rawAvailableDevices = availableFilters.Devices.filter((d) => d !== ALL_FILTER);
  const deviceGroupMap = createDeviceGroupMap(rawAvailableDevices);
  const normalizedAvailableDevices = [
    ...(availableFilters.Devices.includes(ALL_FILTER) ? [ALL_FILTER] : []),
    ...deviceGroupMap.keys(),
  ];
  const normalizedActiveDevices = activeFilters.Devices.includes(ALL_FILTER)
    ? [ALL_FILTER]
    : [...new Set(activeFilters.Devices.map(normalizeDeviceName))];

  function updateActiveFilters(
    currentValue: AccountActivityFilters,
    property: keyof Omit<AccountActivityFilters, 'Skip' | 'Top'>,
    value: string,
  ) {
    const values = { ...currentValue };

    if (property === 'Devices' && value !== ALL_FILTER) {
      const current = values.Devices as string[];
      const hasAllOption = availableFilters.Devices.includes(ALL_FILTER);
      values.Devices = computeDeviceFilterUpdate(current, value, deviceGroupMap, hasAllOption);
    } else {
      const hasAllValueOption = availableFilters[property].includes(ALL_FILTER);
      const current = values[property] as string[];
      values[property] = computePropertyUpdate(current, value, property, hasAllValueOption);
    }

    navigate({
      replace: true,
      search: {
        activities: values.Activities,
        orders: values.Orders,
        devices: values.Devices,
        applications: values.Applications,
      },
    });
    setDirty(true);
  }

  const props: AccountActivityFilterProps = {
    activeFilters: activeFilters,
    availableFilters: { ...availableFilters, Devices: normalizedAvailableDevices },
    normalizedActiveDevices: normalizedActiveDevices,
    applicationNames:
      applicationsNamesData ?? ({} as AccountActivityFilterProps['applicationNames']),
    isLoading: isAccountActivityLoading || isApplicationsNamesLoading,
    onChangeFilter: updateActiveFilters,
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: AccountActivityFilterProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <AccountActivityFiltersLight {...props} />,
  };
}
